import React, { Fragment, useContext, useEffect, useState } from 'react'
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react'
import { MdLanguage, MdOutlineWhatsapp } from 'react-icons/md'
import { useStaticQuery, graphql } from 'gatsby'

import { formatWhatsAppLink } from 'services/formatter'
import { classNames } from 'services/string'
import { WhatsAppContext } from './WhatsAppContext'
import { TintimLink } from './TintimLink'

// Default rounded WhatsApp button styles
const roundedWhatsAppButtonDefaultStyles = classNames(
  'flex justify-center items-center float-right',
  'fixed bottom-4 right-4 z-[10000]',
  'mt-2 px-3 h-14 w-14 rounded-full',
  'bg-whatsapp font-xl drop-shadow-lg shadow-lg cursor-pointer',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-800',
)

interface BaseProps {
  className?: string
  onClick?: () => unknown
  children?: React.ReactNode
}

interface WhatsAppButtonProps extends BaseProps {
  url: string
  webUrl?: string
}

interface WhatsAppTargetDialogProps extends WhatsAppButtonProps {
  isOpen: boolean
  onClose: () => unknown
}

export const WhatsAppTargetDialog = ({ isOpen, onClose, url, onClick, webUrl }: WhatsAppTargetDialogProps) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <HeadlessDialog as='div' className='relative z-[10001]' onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black/40 backdrop-blur-sm' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <HeadlessDialog.Panel className='w-full max-w-md md:max-w-xs transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all'>
                <HeadlessDialog.Title as='h3' className='text-lg font-medium leading-6 text-gray-900'>
                  Conversar pelo WhatsApp
                </HeadlessDialog.Title>
                <div className='mt-2'>
                  <p className='text-sm text-gray-500'>Por onde você gostaria de iniciar a conversa?</p>
                </div>

                <div className='flex flex-col gap-4 mt-6 md:mt-8 w-full'>
                  <TintimLink
                    url={url}
                    onClick={onClick}
                    className='rounded-md font-medium drop-shadow-md transition-all duration-200 flex flex-row gap-2 items-center justify-center w-full bg-whatsapp hover:bg-whatsapp/80 font-sm px-3.5 py-2 text-sm cursor-pointer'
                    rel='noreferrer noopener'
                    target='_BLANK'
                    aria-label='Link do WhatsApp'
                  >
                    <MdOutlineWhatsapp aria-label='Ícone do Whatsapp' size='24' className='text-black' />
                    App do WhatsApp
                  </TintimLink>

                  <div className='w-full mt-2'>
                    <hr />
                    <div className='w-full flex justify-center'>
                      <p className='-mt-2 bg-white w-fit text-black/40 text-xs uppercase px-2'>ou</p>
                    </div>
                  </div>

                  <TintimLink
                    url={webUrl || url}
                    onClick={onClick}
                    className='rounded-md font-medium drop-shadow-md transition-all duration-200 flex flex-row gap-2 items-center justify-center w-full bg-whatsapp hover:bg-whatsapp/80 font-sm px-3.5 py-2 text-sm cursor-pointer'
                    rel='noreferrer noopener'
                    target='_BLANK'
                    aria-label='Link do WhatsApp'
                  >
                    <MdLanguage aria-label='Ícone do Whatsapp' size='24' className='text-black' />
                    WhatsApp Web
                  </TintimLink>
                </div>
              </HeadlessDialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  )
}

const MobileButton = ({ className, url, onClick, children }: WhatsAppButtonProps) => {
  return children ? (
    <div className={className}>
      <a href={url} rel='noreferrer noopener' target='_BLANK' aria-label='Link do WhatsApp' onClick={onClick}>
        {children}
      </a>
    </div>
  ) : (
    <a
      href={url}
      rel='noreferrer noopener'
      target='_BLANK'
      aria-label='Link do WhatsApp'
      className={roundedWhatsAppButtonDefaultStyles}
      onClick={onClick}
    >
      <MdOutlineWhatsapp aria-label='Ícone do WhatsApp' size='32' className='text-white' />
    </a>
  )
}

const WebButton = ({ className, url, webUrl, onClick: onClickCallback, children }: WhatsAppButtonProps) => {
  const [isWhatsAppTargetDialogOpen, setIsWhatsAppTargetDialogOpen] = useState<boolean>(false)

  return (
    <div>
      {children && (
        <div className={className}>
          <div aria-label='Link do WhatsApp' onClick={() => setIsWhatsAppTargetDialogOpen(true)}>
            {children}
          </div>
        </div>
      )}
      {!children && (
        <div
          aria-label='Link do WhatsApp'
          className={roundedWhatsAppButtonDefaultStyles}
          onClick={() => setIsWhatsAppTargetDialogOpen(true)}
        >
          <MdOutlineWhatsapp aria-label='Ícone do Whatsapp' size='32' className='text-white' />
        </div>
      )}

      <WhatsAppTargetDialog
        isOpen={isWhatsAppTargetDialogOpen}
        onClick={onClickCallback}
        onClose={() => setIsWhatsAppTargetDialogOpen(false)}
        url={url}
        webUrl={webUrl}
      />
    </div>
  )
}

const WhatsAppButton = (props: BaseProps) => {
  const {
    customMessage,
    customMessageSlug: slug,
    whatsAppUrl: customWhatsAppUrl,
    whatsAppWebUrl: customWhatsAppWebUrl,
  } = useContext(WhatsAppContext)

  const { strapiGlobal } = useStaticQuery(graphql`
    query {
      strapiGlobal {
        linkWhatsApp
        linkWhatsAppWeb
        linkWhatsAppQueryParameters
      }
    }
  `)

  const { linkWhatsApp, linkWhatsAppWeb, linkWhatsAppQueryParameters } = strapiGlobal || {}

  const baseWhatsAppUrl = linkWhatsApp + '?' + linkWhatsAppQueryParameters
  const baseWhatsAppWebUrl = (linkWhatsAppWeb || linkWhatsApp) + '?' + linkWhatsAppQueryParameters

  const whatsAppLink = customWhatsAppUrl ? customWhatsAppUrl : formatWhatsAppLink(baseWhatsAppUrl, { slug, customMessage })
  const whatsAppWebLink = customWhatsAppWebUrl ? customWhatsAppWebUrl : formatWhatsAppLink(baseWhatsAppWebUrl, { slug, customMessage })

  const userAgent = typeof window !== 'undefined' ? (navigator ? navigator.userAgent : undefined) : undefined

  // The strategy of mobile device recognization applied below is based on a recommendation from MDN Web Docs
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
  const isMobile = userAgent?.includes('Mobi')

  const sharedProps: WhatsAppButtonProps = {
    ...props,
    url: whatsAppLink,
    webUrl: whatsAppWebLink,
  }

  return isMobile ? <MobileButton {...sharedProps} /> : <WebButton {...sharedProps} />
}

export default WhatsAppButton
